import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

console.log('Supabase URL:', supabaseUrl);
console.log('Supabase Key:', supabaseKey);

if (!supabaseUrl || !supabaseKey) {
  throw new Error('Missing Supabase credentials');
}

export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});

// Inicializar cliente anônimo
const initializeAnonUser = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  
  if (!session) {
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: 'anonymous@example.com',
      password: 'anonymous123'
    });
    
    if (signInError) {
      console.error('Erro ao fazer login anônimo:', signInError);
    }
  }
};

initializeAnonUser();

export interface FinancialTransaction {
  id: string;
  description: string;
  amount: number;
  category: string;
  type: 'Saldo' | 'Despesas' | 'Receita' | 'Salário' | 'Carro';
  owner: 'Luiz' | 'Empresa';
  status: 'Done' | 'Not started' | 'In progress';
  date: string;
  created_at?: string;
}

export interface FinancialGoal {
  id: string;
  name: string;
  description?: string;
  current: number;
  target: number;
  color: string;
  category?: string;
  deadline?: string;
  status?: 'pending' | 'completed';
  created_at?: string;
}

export interface Goal {
  id: string;
  user_id: string;
  title: string;
  description?: string;
  target_amount: number;
  current_amount: number;
  deadline: string;
  status: 'pending' | 'completed';
  created_at: string;
}

export class FinanceService {
  async getTransactions() {
    try {
      console.log('Buscando transações...');
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .order('date', { ascending: false });

      if (error) {
        console.error('Erro ao buscar transações:', error);
        throw error;
      }
      
      console.log('Transações encontradas:', data);
      return data;
    } catch (error) {
      console.error('Erro no getTransactions:', error);
      throw error;
    }
  }

  async addTransaction(transaction: Omit<FinancialTransaction, 'id' | 'created_at'>) {
    try {
      console.log('Adicionando transação:', transaction);
      const { data, error } = await supabase
        .from('transactions')
        .insert([transaction])
        .select()
        .single();

      if (error) {
        console.error('Erro ao adicionar transação:', error);
        throw error;
      }

      console.log('Transação adicionada:', data);
      return data;
    } catch (error) {
      console.error('Erro no addTransaction:', error);
      throw error;
    }
  }

  async updateTransaction(id: string, updates: Partial<FinancialTransaction>) {
    try {
      console.log('Atualizando transação:', id, updates);
      const { data, error } = await supabase
        .from('transactions')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Erro ao atualizar transação:', error);
        throw error;
      }

      console.log('Transação atualizada:', data);
      return data;
    } catch (error) {
      console.error('Erro no updateTransaction:', error);
      throw error;
    }
  }

  async deleteTransaction(id: string) {
    try {
      console.log('Deletando transação:', id);
      const { error } = await supabase
        .from('transactions')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Erro ao deletar transação:', error);
        throw error;
      }

      console.log('Transação deletada com sucesso');
      return true;
    } catch (error) {
      console.error('Erro no deleteTransaction:', error);
      throw error;
    }
  }

  async getBalance() {
    try {
      console.log('Calculando saldo...');
      const { data, error } = await supabase
        .from('transactions')
        .select('amount, type');

      if (error) {
        console.error('Erro ao buscar saldo:', error);
        throw error;
      }

      const balance = data.reduce((acc, curr) => {
        if (curr.type === 'Receita' || curr.type === 'Saldo' || curr.type === 'Salário') {
          return acc + curr.amount;
        } else {
          return acc - curr.amount;
        }
      }, 0);

      console.log('Saldo calculado:', balance);
      return balance;
    } catch (error) {
      console.error('Erro no getBalance:', error);
      throw error;
    }
  }

  async getMonthlyReport() {
    try {
      console.log('Gerando relatório mensal...');
      const { data: transactions, error } = await supabase
        .from('transactions')
        .select('*')
        .gte('date', new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString())
        .lte('date', new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString());

      if (error) {
        console.error('Erro ao buscar dados para relatório:', error);
        throw error;
      }

      const byCategory = transactions.reduce((acc: Record<string, number>, curr) => {
        if (curr.type === 'Despesas') {
          acc[curr.category] = (acc[curr.category] || 0) + curr.amount;
        }
        return acc;
      }, {});

      const monthlyGoals = [
        { name: 'Economia', current: 2000, target: 3000, color: '#1AD1A5' },
        { name: 'Investimentos', current: 1500, target: 2000, color: '#4C6EF5' },
        { name: 'Fundo de Emergência', current: 5000, target: 10000, color: '#FF922B' },
      ];

      const report = { byCategory, monthlyGoals };
      console.log('Relatório gerado:', report);
      return report;
    } catch (error) {
      console.error('Erro no getMonthlyReport:', error);
      throw error;
    }
  }

  async getGoals() {
    try {
      const { data, error } = await supabase
        .from('goals')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao buscar metas:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Erro no getGoals:', error);
      throw error;
    }
  }

  async createGoal(goal: Omit<FinancialGoal, 'id' | 'created_at'>) {
    // Mapear os campos para corresponder à estrutura da tabela
    const mappedGoal = {
      name: goal.name,
      description: goal.description,
      current: goal.current,
      target: goal.target,
      color: goal.color,
      category: goal.category,
      deadline: goal.deadline,
      status: goal.status || 'pending'
    };

    const { data, error } = await supabase
      .from('goals')
      .insert([mappedGoal])
      .select()
      .single();

    if (error) {
      console.error('Erro ao criar meta:', error);
      throw error;
    }

    return data;
  }

  async updateGoal(id: string, updates: Partial<FinancialGoal>) {
    // Mapear os campos para corresponder à estrutura da tabela
    const mappedUpdates = {
      name: updates.name,
      description: updates.description,
      current: updates.current,
      target: updates.target,
      color: updates.color,
      category: updates.category,
      deadline: updates.deadline,
      status: updates.status
    };

    // Remover campos undefined
    Object.keys(mappedUpdates).forEach(key => 
      mappedUpdates[key] === undefined && delete mappedUpdates[key]
    );

    const { data, error } = await supabase
      .from('goals')
      .update(mappedUpdates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Erro ao atualizar meta:', error);
      throw error;
    }

    return data;
  }

  async deleteGoal(id: string) {
    const { error } = await supabase
      .from('goals')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  async completeGoal(id: string) {
    const { data, error } = await supabase
      .from('goals')
      .update({ status: 'completed' })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async getFinancialStats() {
    try {
      const transactions = await this.getTransactions();
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();

      // Filtrar transações do mês atual
      const monthTransactions = transactions.filter(t => {
        const date = new Date(t.date);
        return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
      });

      // Calcular médias por categoria
      const categoryAverages = transactions.reduce((acc: Record<string, { total: number; count: number }>, t) => {
        if (t.type === 'Despesas') {
          if (!acc[t.category]) {
            acc[t.category] = { total: 0, count: 0 };
          }
          acc[t.category].total += t.amount;
          acc[t.category].count++;
        }
        return acc;
      }, {});

      // Encontrar dias com maiores gastos
      const dailyExpenses = transactions.reduce((acc: Record<string, number>, t) => {
        if (t.type === 'Despesas') {
          const date = new Date(t.date).toISOString().split('T')[0];
          acc[date] = (acc[date] || 0) + t.amount;
        }
        return acc;
      }, {});

      const topExpenseDays = Object.entries(dailyExpenses)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5);

      return {
        categoryAverages: Object.entries(categoryAverages).map(([category, data]) => ({
          category,
          average: data.total / data.count,
          total: data.total,
          count: data.count
        })),
        topExpenseDays,
        monthlyStats: {
          totalIncome: monthTransactions
            .filter(t => t.type === 'Receita' || t.type === 'Salário')
            .reduce((sum, t) => sum + t.amount, 0),
          totalExpenses: monthTransactions
            .filter(t => t.type === 'Despesas')
            .reduce((sum, t) => sum + t.amount, 0),
          largestExpense: Math.max(...monthTransactions
            .filter(t => t.type === 'Despesas')
            .map(t => t.amount)),
          transactionCount: monthTransactions.length
        }
      };
    } catch (error) {
      console.error('Erro ao calcular estatísticas:', error);
      throw error;
    }
  }
}

export const financeService = new FinanceService();
